/**
 * Cantera Stats v2 — design tokens
 *
 * Centralizes the visual language for the live/video stats screens.
 * Couleurs club (primary/secondary) sont consommées via ClubThemeContext.
 *
 * Tous les composants v2 doivent passer par useStatsTheme() pour recuperer
 * surfaces, text, action colors, etc. Aucune valeur hex ne doit etre ecrite
 * en dur dans les composants v2.
 */

import { useMemo } from 'react';
import { useClubTheme } from '../../../contexts/ClubThemeContext';

// Surfaces — hierarchie a 3 niveaux pour le mode immersif sombre
export const STATS_SURFACES = {
  base: '#0F1419',       // Fond global, zones video
  elevated: '#1B2129',   // Panneaux d'action
  floating: '#252C36',   // Cards joueurs, modales
  border: 'rgba(255,255,255,0.08)',
  divider: 'rgba(255,255,255,0.06)',
  overlay: 'rgba(15,20,25,0.72)',
};

// Texte — hierarchie WCAG AA sur fond sombre
export const STATS_TEXT = {
  primary: '#F5F7FA',
  secondary: '#9BA4B0',
  muted: '#6B7280',
  inverse: '#0F1419',
  danger: '#FCA5A5',
};

// Couleurs semantiques par categorie d'action
// (alignees sur actionUtils.js mais lissees pour AA sur fond sombre)
export const STATS_ACTION_COLORS = {
  made: '#22C55E',
  missed: '#EF4444',
  neutral: '#3B82F6',
  warning: '#F59E0B',
  info: '#06B6D4',
  rebound: {
    off: '#60A5FA',
    def: '#22D3EE',
  },
  ballHandling: {
    ast: '#FB923C',
    tov: '#F472B6',
    stl: '#A78BFA',
  },
  defense: {
    blk: '#14B8A6',
    foul: '#F97316',
    tech: '#991B1B',
  },
  opponent: '#7F1D1D',
  tag: '#3B82F6',
};

// Geometrie
export const STATS_RADIUS = {
  sm: 6,
  md: 10,
  lg: 14,
  xl: 20,
  pill: 999,
};

export const STATS_SPACING = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
};

// Profondeur — elevation par opacite plutot qu'ombres dures
export const STATS_SHADOWS = {
  sm: '0 1px 2px rgba(0,0,0,0.4)',
  md: '0 4px 12px rgba(0,0,0,0.45)',
  lg: '0 12px 32px rgba(0,0,0,0.55)',
  inset: 'inset 0 1px 0 rgba(255,255,255,0.04)',
};

// Mouvement — toutes les transitions partagent la meme courbe
export const STATS_MOTION = {
  fast: '120ms cubic-bezier(0.2, 0.8, 0.2, 1)',
  base: '200ms cubic-bezier(0.2, 0.8, 0.2, 1)',
  slow: '320ms cubic-bezier(0.2, 0.8, 0.2, 1)',
  spring: '420ms cubic-bezier(0.34, 1.56, 0.64, 1)',
};

// Typographie — base sur la stack systeme pour la performance
export const STATS_TYPOGRAPHY = {
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  display: { size: 80, weight: 700, letterSpacing: '-0.02em' },
  h1: { size: 32, weight: 700 },
  h2: { size: 24, weight: 700 },
  h3: { size: 18, weight: 600 },
  body: { size: 14, weight: 400 },
  caption: { size: 12, weight: 500 },
  micro: { size: 10, weight: 600, letterSpacing: '0.04em' },
};

// Sizing minimum tactile (WCAG)
export const STATS_TOUCH = {
  minTarget: 44,
  buttonHeight: 48,
  iconButton: 40,
};

/**
 * Utilitaire — convertit un hex en rgba(string)
 */
export const withAlpha = (hex, alpha) => {
  if (!hex || !hex.startsWith('#')) return hex;
  const num = parseInt(hex.slice(1), 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Hook principal — retourne tous les tokens + accents derives du club
 */
export function useStatsTheme() {
  const club = useClubTheme();

  return useMemo(() => ({
    surfaces: STATS_SURFACES,
    text: STATS_TEXT,
    actionColors: STATS_ACTION_COLORS,
    radius: STATS_RADIUS,
    spacing: STATS_SPACING,
    shadows: STATS_SHADOWS,
    motion: STATS_MOTION,
    typography: STATS_TYPOGRAPHY,
    touch: STATS_TOUCH,

    // Accent club (calcule depuis ClubThemeContext)
    accent: club.primaryColor,
    accentSecondary: club.secondaryColor,
    accentSoft: withAlpha(club.primaryColor, 0.12),
    accentSofter: withAlpha(club.primaryColor, 0.06),
    accentBorder: withAlpha(club.primaryColor, 0.32),
    accentStrong: withAlpha(club.primaryColor, 0.85),
    gradient: `linear-gradient(135deg, ${club.primaryColor} 0%, ${club.secondaryColor} 100%)`,

    // Identite club
    clubName: club.clubName,
    logo: club.logo,

    // Helper
    withAlpha,
  }), [club.primaryColor, club.secondaryColor, club.clubName, club.logo]);
}
